// @ts-nocheck
import { FC, useState, useEffect, useRef } from 'react';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { ExpandMore } from '@mui/icons-material';
import { Box, TextField } from '@mui/material';
import './select.scss';
import { AutoSizer } from 'react-virtualized';

interface CommonSelectProps {
  options: { value: string | number; label: string; }[]; // options should have value and label
  value?: string | number;
  defaultValue?: any;
  onChange: (event: SelectChangeEvent<string | number>) => void;  // This expects SelectChangeEvent
  placeholder?: string;
  disabled?: boolean;
  searchPlaceholder?: string;
  readOnly?: boolean
}

const MinSingleSelect: FC<CommonSelectProps> = ({
  options,
  value,
  defaultValue,
  onChange,
  placeholder,
  disabled = false,
  searchPlaceholder,
  readOnly = false
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const selectRef = useRef<HTMLDivElement | null>(null);

  // Update filtered options when options or search query change
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredOptions(options);
    } else {
      const lowercasedQuery = searchQuery.toLowerCase();
      setFilteredOptions(
        options.filter(option =>
          option.label.toLowerCase().includes(lowercasedQuery)
        )
      );
    }
  }, [searchQuery, options]);

  // Focus the search input after dropdown opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus(); // Ensure the search input gets focused once dropdown opens
      }, 250); // Timeout to ensure dropdown is fully opened before focusing
    }
  }, [open]);

  const handleOpen = () => {
    setOpen(true); // Set the dropdown open
  };

  const handleClose = () => {
    setOpen(false); // Close the dropdown when an option is selected
  };

  const handleChange = (event: SelectChangeEvent<string | number>) => {
    onChange(event);
    setSearchQuery(''); // Clear the search input on select
    setOpen(false); // Close the dropdown
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleMenuItemClick = (selectedValue: string | number) => {
    handleChange({ target: { value: selectedValue } } as SelectChangeEvent<string | number>);
    setSearchQuery(''); // Clear the search input
    setOpen(false); // Close the dropdown after selection
  };

  // Prevent the dropdown from closing when clicking inside the dropdown or search field
  const handleDropdownClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen(true);
  };

  return (
    <FormControl fullWidth className={`${disabled ? "disabled" : ""} min-select-wrapper`}>
      <Select
        readOnly={readOnly}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        IconComponent={ExpandMore}
        className="single-select"
        disabled={disabled}
        placeholder={placeholder}
        MenuProps={{
          PaperProps: { className: 'single-select-menu' },
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
          disableScrollLock: true,
          disablePortal: true,
        }}
        renderValue={(selected) => {
          // Find the selected option based on value
          const selectedOption = options.find(option => option.value === selected);
          return selectedOption ? selectedOption.label : placeholder || "Select an option";
        }}
        ref={selectRef}
      >
        {/* Search input */}
        <Box sx={{ p: 1 }} className="search-wrapper">
          <TextField
            inputRef={inputRef}
            fullWidth
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={searchPlaceholder || 'Search...'}
            variant="outlined"
            size="small"
            disabled={disabled}
            autoComplete="off"
            onClick={handleDropdownClick}
          />
        </Box>

        {/* Auto-sized options list */}
        <AutoSizer disableHeight>
          {({ width }: { width: number }) => (
            <div style={{ width }} className="list-wrap single">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    onClick={() => handleMenuItemClick(option.value)}
                    selected={value === option.value}
                  >
                    {option.label}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No options found</MenuItem>
              )}
            </div>
          )}
        </AutoSizer>
      </Select>
    </FormControl>
  );
};

export default MinSingleSelect;
